import { inject, Injectable } from '@angular/core';
import { Observable, Subject, tap } from 'rxjs';
import { IResponse } from '../../../../core/interfaces/IResponse';
import { ApiService } from '../../../../core/services/ApiService';
import { IAcardoValidateResult } from '../interfaces/IAcardoValidateResult';
import { IApplyDistributorResult } from '../interfaces/IApplyDistributorResult';
import { IVoucherInfo } from '../interfaces/IVoucherInfo';
import { IVoucherTransaction } from '../interfaces/IVoucherTransaction';
import { IVoucherType } from '../interfaces/IVoucherType';
import { VouchersStore } from '../stores/VouchersStore';
import { IKioskProduct } from '../../../kiosk/core/interfaces/IKioskProduct';

@Injectable({
  providedIn: 'root'
})
export class VouchersService {

  public readonly voucherToAdd$ = new Subject<IVoucherType>();

  private readonly _basePath = '/vouchers';
  private readonly _apiService = inject(ApiService);
  private readonly _vouchersStore = inject(VouchersStore);

  public getVouchers(): Observable<IResponse<IVoucherType[]>> {
    return this._apiService.get(this._basePath)
      .pipe(
        tap(res => this._vouchersStore.voucherTypes.set(res.data))
      );
  }

  public getExternalVouchers(): Observable<IResponse<IVoucherType[]>> {
    return this._apiService.get(`${this._basePath}/external`)
      .pipe(
        tap(res => this._vouchersStore.externalVoucherTypes.set(res.data))
      );
  }

  public getVoucherByNumber(voucherNr: string): Observable<IResponse<IVoucherInfo>> {
    return this._apiService.get(`${this._basePath}/by-number/${voucherNr}`);
  }

  public getVoucherTransactionsByNumber(voucherNr: string): Observable<IResponse<IVoucherTransaction[]>> {
    return this._apiService.get(`${this._basePath}/by-number/${voucherNr}/transactions`);
  }

  public invalidateVoucher(voucherNr: string): Observable<IResponse<void>> {
    return this._apiService.post(`${this._basePath}/by-number/${voucherNr}/invalidate`);
  }

  public validateAcardoVoucher(
    voucherTypeId: number | null,
    voucherCode: string,
    showId?: number,
    valueCents?: number
  ): Observable<IResponse<IAcardoValidateResult>> {
    const body: { voucherCode: string; voucherTypeId?: number; showId?: number; valueCents?: number } = {
      voucherCode: voucherCode.trim()
    };

    if (voucherTypeId != null) {
      body.voucherTypeId = voucherTypeId;
    }

    if (showId != null && showId > 0) {
      body.showId = showId;
    }

    if (valueCents != null) {
      body.valueCents = valueCents;
    }

    return this._apiService.post(`${this._basePath}/external/acardo/validate`, body);
  }

  public applyDistributorVoucher(
    voucherTypeId: number,
    price?: number
  ): Observable<IResponse<IApplyDistributorResult>> {
    const body: { voucherTypeId: number; price?: number } = { voucherTypeId };

    if (price != null) {
      body.price = price;
    }

    return this._apiService.post(`${this._basePath}/external/distributor/apply`, body);
  }
}
